import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  detectPreferredLanguage, 
  extractLanguageFromPath,
  DEFAULT_LANGUAGE,
  type SupportedLanguage
} from '@config/i18n.config';

/**
 * Component that handles language detection and redirection
 * Redirects users from / to /[lang]/ based on their preferences
 */
export function LanguageRedirect() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const pathname = location.pathname;
    const currentLang = extractLanguageFromPath(pathname);

    // If we're at the root without a language prefix, detect and redirect
    if (pathname === '/') {
      const preferredLang = detectPreferredLanguage();
      navigate(`/${preferredLang}`, { replace: true });
      return;
    }

    // If no language detected in path but not at root, redirect to default with current path
    if (!currentLang && pathname !== '/') {
      const preferredLang = detectPreferredLanguage();
      navigate(`/${preferredLang}${pathname}`, { replace: true });
      return;
    }

    // Store the current language in localStorage for persistence
    if (currentLang) {
      try {
        localStorage.setItem('finanzly-language', currentLang);
      } catch (error) {
        console.warn('Failed to save language to localStorage:', error);
      }
    }
  }, [location.pathname, navigate]);

  return null;
}

/**
 * Hook to get the current language from the URL
 */
export function useCurrentLanguage(): SupportedLanguage {
  const location = useLocation();
  return extractLanguageFromPath(location.pathname) || DEFAULT_LANGUAGE;
}

/**
 * Hook to navigate to a path in a specific language
 */
export function useLanguageNavigate() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentLang = useCurrentLanguage();

  return {
    navigateTo: (path: string, language?: SupportedLanguage) => {
      const lang = language || currentLang;
      const cleanPath = path.startsWith('/') ? path : `/${path}`;
      navigate(`/${lang}${cleanPath}`);
    },
    switchLanguage: (newLanguage: SupportedLanguage) => {
      // Save current scroll position
      const currentScrollY = window.scrollY;
      
      const currentPath = location.pathname.replace(`/${currentLang}`, '') || '/';
      navigate(`/${newLanguage}${currentPath}`, { replace: false });
      
      // Restore scroll position after navigation
      setTimeout(() => {
        window.scrollTo(0, currentScrollY);
      }, 0);
    },
    currentLanguage: currentLang
  };
}
